// Calendar mock data
export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  start: Date;
  end: Date;
  allDay: boolean;
  type: 'meeting' | 'task' | 'reminder' | 'event' | 'holiday';
  color: string;
  location?: string;
  attendees?: EventAttendee[];
  organizer: string;
  organizerName: string;
  isRecurring?: boolean;
  recurringPattern?: 'daily' | 'weekly' | 'monthly';
  status: 'confirmed' | 'tentative' | 'cancelled';
  reminders?: number[]; // minutes before
}

export interface EventAttendee {
  id: string;
  name: string;
  email: string;
  status: 'accepted' | 'declined' | 'tentative' | 'pending';
  avatar?: string;
}

export interface TimeSlot {
  start: Date;
  end: Date;
  available: boolean;
  userId?: string;
}

export interface MeetingRoom {
  id: string;
  name: string;
  capacity: number;
  location: string;
  amenities: string[];
  available: boolean;
}

// Generate dates relative to today
const today = new Date();
const getDate = (daysOffset: number, hours: number = 0, minutes: number = 0) => {
  const date = new Date(today);
  date.setDate(date.getDate() + daysOffset);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

export const mockEvents: CalendarEvent[] = [
  {
    id: 'event-1',
    title: 'Team Standup',
    description: 'Daily engineering team standup meeting',
    start: getDate(0, 9, 0),
    end: getDate(0, 9, 30),
    allDay: false,
    type: 'meeting',
    color: 'blue',
    location: 'Conference Room A',
    attendees: [
      { id: 'u1', name: 'John Doe', email: 'john.doe@company.com', status: 'accepted', avatar: '/avatars/1.jpg' },
      { id: 'u2', name: 'Jane Smith', email: 'jane.smith@company.com', status: 'accepted', avatar: '/avatars/2.jpg' },
      { id: 'u4', name: 'Bob Williams', email: 'bob.williams@company.com', status: 'accepted', avatar: '/avatars/4.jpg' },
      { id: 'u5', name: 'Charlie Brown', email: 'charlie.brown@company.com', status: 'tentative', avatar: '/avatars/5.jpg' },
    ],
    organizer: 'u2',
    organizerName: 'Jane Smith',
    isRecurring: true,
    recurringPattern: 'daily',
    status: 'confirmed',
    reminders: [10],
  },
  {
    id: 'event-2',
    title: 'Product Review',
    description: 'Weekly product review with stakeholders',
    start: getDate(0, 14, 0),
    end: getDate(0, 15, 30),
    allDay: false,
    type: 'meeting',
    color: 'violet',
    location: 'Main Conference Room',
    attendees: [
      { id: 'u1', name: 'John Doe', email: 'john.doe@company.com', status: 'accepted', avatar: '/avatars/1.jpg' },
      { id: 'u6', name: 'Diana Prince', email: 'diana.prince@company.com', status: 'accepted', avatar: '/avatars/6.jpg' },
      { id: 'u3', name: 'Alice Johnson', email: 'alice.johnson@company.com', status: 'accepted', avatar: '/avatars/3.jpg' },
    ],
    organizer: 'u6',
    organizerName: 'Diana Prince',
    isRecurring: true,
    recurringPattern: 'weekly',
    status: 'confirmed',
    reminders: [15, 60],
  },
  {
    id: 'event-3',
    title: 'Client Call - TechCorp',
    description: 'Quarterly review with TechCorp stakeholders',
    start: getDate(1, 10, 0),
    end: getDate(1, 11, 0),
    allDay: false,
    type: 'meeting',
    color: 'green',
    location: 'Zoom',
    attendees: [
      { id: 'u13', name: 'Kevin Hart', email: 'kevin.hart@company.com', status: 'accepted', avatar: '/avatars/13.jpg' },
      { id: 'u15', name: 'Michael Scott', email: 'michael.scott@company.com', status: 'accepted', avatar: '/avatars/15.jpg' },
    ],
    organizer: 'u13',
    organizerName: 'Kevin Hart',
    status: 'confirmed',
    reminders: [30],
  },
  {
    id: 'event-4',
    title: 'Sprint Planning',
    description: 'Plan tasks for the upcoming sprint',
    start: getDate(1, 13, 0),
    end: getDate(1, 15, 0),
    allDay: false,
    type: 'meeting',
    color: 'cyan',
    location: 'Engineering Room',
    attendees: [
      { id: 'u1', name: 'John Doe', email: 'john.doe@company.com', status: 'accepted', avatar: '/avatars/1.jpg' },
      { id: 'u2', name: 'Jane Smith', email: 'jane.smith@company.com', status: 'accepted', avatar: '/avatars/2.jpg' },
      { id: 'u4', name: 'Bob Williams', email: 'bob.williams@company.com', status: 'accepted', avatar: '/avatars/4.jpg' },
      { id: 'u5', name: 'Charlie Brown', email: 'charlie.brown@company.com', status: 'accepted', avatar: '/avatars/5.jpg' },
      { id: 'u7', name: 'Ethan Hunt', email: 'ethan.hunt@company.com', status: 'pending', avatar: '/avatars/7.jpg' },
    ],
    organizer: 'u2',
    organizerName: 'Jane Smith',
    isRecurring: true,
    recurringPattern: 'weekly',
    status: 'confirmed',
    reminders: [15],
  },
  {
    id: 'event-5',
    title: 'Design Review',
    description: 'Review new dashboard designs',
    start: getDate(2, 11, 0),
    end: getDate(2, 12, 0),
    allDay: false,
    type: 'meeting',
    color: 'pink',
    location: 'Design Studio',
    attendees: [
      { id: 'u1', name: 'John Doe', email: 'john.doe@company.com', status: 'accepted', avatar: '/avatars/1.jpg' },
      { id: 'u3', name: 'Alice Johnson', email: 'alice.johnson@company.com', status: 'accepted', avatar: '/avatars/3.jpg' },
      { id: 'u8', name: 'Fiona Green', email: 'fiona.green@company.com', status: 'accepted', avatar: '/avatars/8.jpg' },
      { id: 'u10', name: 'Hannah Davis', email: 'hannah.davis@company.com', status: 'tentative', avatar: '/avatars/10.jpg' },
    ],
    organizer: 'u10',
    organizerName: 'Hannah Davis',
    status: 'confirmed',
    reminders: [15],
  },
  {
    id: 'event-6',
    title: 'Submit Q4 Report',
    start: getDate(3, 17, 0),
    end: getDate(3, 17, 0),
    allDay: false,
    type: 'task',
    color: 'orange',
    organizer: 'u1',
    organizerName: 'John Doe',
    status: 'confirmed',
    reminders: [60, 1440],
  },
  {
    id: 'event-7',
    title: 'Company All-Hands',
    description: 'Quarterly all-hands meeting with leadership updates',
    start: getDate(5, 15, 0),
    end: getDate(5, 16, 30),
    allDay: false,
    type: 'event',
    color: 'red',
    location: 'Main Auditorium',
    organizer: 'u14',
    organizerName: 'Laura Palmer',
    status: 'confirmed',
    reminders: [60],
  },
  {
    id: 'event-8',
    title: 'Holiday - Presidents Day',
    start: getDate(7, 0, 0),
    end: getDate(7, 23, 59),
    allDay: true,
    type: 'holiday',
    color: 'gray',
    organizer: 'u14',
    organizerName: 'Laura Palmer',
    status: 'confirmed',
  },
  {
    id: 'event-9',
    title: '1:1 with Manager',
    description: 'Weekly check-in with Jane',
    start: getDate(0, 16, 0),
    end: getDate(0, 16, 30),
    allDay: false,
    type: 'meeting',
    color: 'teal',
    location: 'Jane\'s Office',
    attendees: [
      { id: 'u1', name: 'John Doe', email: 'john.doe@company.com', status: 'accepted', avatar: '/avatars/1.jpg' },
      { id: 'u2', name: 'Jane Smith', email: 'jane.smith@company.com', status: 'accepted', avatar: '/avatars/2.jpg' },
    ],
    organizer: 'u2',
    organizerName: 'Jane Smith',
    isRecurring: true,
    recurringPattern: 'weekly',
    status: 'confirmed',
    reminders: [5],
  },
  {
    id: 'event-10',
    title: 'Interview - Frontend Developer',
    description: 'Technical interview for senior frontend position',
    start: getDate(2, 14, 0),
    end: getDate(2, 15, 0),
    allDay: false,
    type: 'meeting',
    color: 'yellow',
    location: 'Interview Room 1',
    attendees: [
      { id: 'u1', name: 'John Doe', email: 'john.doe@company.com', status: 'accepted', avatar: '/avatars/1.jpg' },
      { id: 'u2', name: 'Jane Smith', email: 'jane.smith@company.com', status: 'accepted', avatar: '/avatars/2.jpg' },
      { id: 'u14', name: 'Laura Palmer', email: 'laura.palmer@company.com', status: 'accepted', avatar: '/avatars/14.jpg' },
    ],
    organizer: 'u14',
    organizerName: 'Laura Palmer',
    status: 'confirmed',
    reminders: [15, 60],
  },
];

export const mockMeetingRooms: MeetingRoom[] = [
  {
    id: 'room-1',
    name: 'Conference Room A',
    capacity: 8,
    location: 'Floor 1',
    amenities: ['TV', 'Whiteboard', 'Video Conferencing'],
    available: true,
  },
  {
    id: 'room-2',
    name: 'Main Conference Room',
    capacity: 20,
    location: 'Floor 2',
    amenities: ['TV', 'Projector', 'Video Conferencing', 'Whiteboard'],
    available: false,
  },
  {
    id: 'room-3',
    name: 'Engineering Room',
    capacity: 12,
    location: 'Floor 1',
    amenities: ['TV', 'Whiteboard', 'Standing Desks'],
    available: true,
  },
  {
    id: 'room-4',
    name: 'Design Studio',
    capacity: 6,
    location: 'Floor 2',
    amenities: ['Apple TV', 'Drawing Tablets', 'Whiteboard'],
    available: true,
  },
  {
    id: 'room-5',
    name: 'Interview Room 1',
    capacity: 4,
    location: 'Floor 1',
    amenities: ['TV', 'Video Conferencing'],
    available: true,
  },
  {
    id: 'room-6',
    name: 'Phone Booth 1',
    capacity: 1,
    location: 'Floor 1',
    amenities: ['Sound Insulation'],
    available: true,
  },
];

// Generate time slots for availability picker
export const generateTimeSlots = (date: Date, userId: string): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  const startHour = 9;
  const endHour = 18;

  for (let hour = startHour; hour < endHour; hour++) {
    for (let half = 0; half < 2; half++) {
      const start = new Date(date);
      start.setHours(hour, half * 30, 0, 0);
      const end = new Date(start);
      end.setMinutes(end.getMinutes() + 30);

      // Simulate some busy slots
      const available = Math.random() > 0.3;

      slots.push({
        start,
        end,
        available,
        userId: available ? undefined : userId,
      });
    }
  }

  return slots;
};

// Calendar stats
export const calendarStats = {
  todayEvents: 4,
  upcomingMeetings: 8,
  pendingInvites: 2,
  recurringMeetings: 4,
};

// Event types for filtering
export const eventTypes = [
  { value: 'meeting', label: 'Meeting', color: 'blue' },
  { value: 'task', label: 'Task', color: 'orange' },
  { value: 'reminder', label: 'Reminder', color: 'yellow' },
  { value: 'event', label: 'Event', color: 'red' },
  { value: 'holiday', label: 'Holiday', color: 'gray' },
];
